//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import {
  Retailer,
  RetailerPdpUrl,
} from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';
import { RedemptionLocationFormPdpUrlsFields } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/fulfillment-section/deals/create-redemption-location/RedemptionLocationFormPdpUrlsFields';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { REDEMPTION_LOCATION_VALIDATION_RULES } from '@campaigns/constants/sampling/SamplingRedemptionLocationValidationRulesConstants';
import { useSamplingCampaign } from '@campaigns/hooks/useSamplingCampaign';
import { FormFieldController } from '@components/form/field-controller/FormFieldController';
import { FormTextAreaInput } from '@components/form/text-area/FormTextAreaInput';
import { FormTextInput } from '@components/form/text-input/FormTextInput';
import { Modal } from '@components/molecules/modal/Modal';
import { isDefined } from '@utils/filterPredicates';

export const CreateRedemptionLocationModal = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { control, getValues, trigger } = useFormContext();
  const {
    closeRedemptionLocationModal,
    isRedemptionLocationModalOpen,
    redemptionLocationToEdit,
    saveLocation,
  } = useSamplingCampaign();

  const isEditRedemptionLocation = isDefined(redemptionLocationToEdit);

  const onSubmit = async () => {
    const isValid = await trigger();

    if (isValid) {
      setIsSubmitting(true);
      const currentRedemptionLocation: Retailer = getValues();
      const validPdpUrls = currentRedemptionLocation.pdpUrls?.filter(
        (pdpUrl: RetailerPdpUrl) => pdpUrl.pdpUrl && pdpUrl.productId,
      );

      const updatedData = {
        ...currentRedemptionLocation,
        pdpUrls: validPdpUrls,
      };

      saveLocation(updatedData);
      closeRedemptionLocationModal();
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      closeModal={closeRedemptionLocationModal}
      contents={
        <Container>
          <RowContainer>
            <FormFieldController
              control={control}
              name='retailerName'
              rules={REDEMPTION_LOCATION_VALIDATION_RULES.retailerName}
            >
              {(props) => (
                <FormTextInput
                  {...props}
                  label='Location name'
                  labelHelpText='Enter the name of the store or company where participants can redeem the code and order the product.'
                  placeholder='Enter location name'
                  trackingId={
                    SAMPLING_CAMPAIGNS_TRACKING_ID.CAMPAIGN_REQUEST_FULFILLMENT_REDEMPTION_LOCATION_NAME_INPUT
                  }
                />
              )}
            </FormFieldController>
            <FormFieldController
              control={control}
              name='locatorLink'
              rules={REDEMPTION_LOCATION_VALIDATION_RULES.locatorLink}
            >
              {(props) => (
                <FormTextInput
                  {...props}
                  label='Store locator URL (optional)'
                  labelHelpText='Enter a store locator web address to help participants find stores near them to purchase or collect an order in person.'
                  placeholder='Enter locator URL'
                  trackingId={
                    SAMPLING_CAMPAIGNS_TRACKING_ID.CAMPAIGN_REQUEST_FULFILLMENT_REDEMPTION_LOCATION_LOCATOR_LINK_INPUT
                  }
                />
              )}
            </FormFieldController>
          </RowContainer>

          <FormFieldController
            control={control}
            name='redemptionGuidance'
            rules={REDEMPTION_LOCATION_VALIDATION_RULES.redemptionGuidance}
          >
            {(props) => (
              <FormTextAreaInput
                {...props}
                label='Code Redemption Guidance'
                labelHelpText='Enter additional information you want participants to know about the code and how to use it.'
                placeholder='Enter redemption guidance'
                trackingId={
                  SAMPLING_CAMPAIGNS_TRACKING_ID.CAMPAIGN_REQUEST_FULFILLMENT_REDEMPTION_LOCATION_REDEMPTION_GUIDANCE_INPUT
                }
              />
            )}
          </FormFieldController>

          <RedemptionLocationFormPdpUrlsFields />
        </Container>
      }
      isFooterEnabled
      isModalOpen={isRedemptionLocationModalOpen}
      primaryButtonInfo={{
        buttonContent: isEditRedemptionLocation
          ? 'Save location'
          : 'Create location',
        trackingId:
          SAMPLING_CAMPAIGNS_TRACKING_ID.REDEMPTION_LOCATION_MODAL_SUBMIT_BUTTON,
        onClick: onSubmit,
        isDisabled: isSubmitting,
      }}
      tertiaryButtonInfo={{
        buttonContent: 'Cancel',
        onClick: closeRedemptionLocationModal,
        trackingId:
          SAMPLING_CAMPAIGNS_TRACKING_ID.REDEMPTION_LOCATION_MODAL_CANCEL_BUTTON,
        isDisabled: isSubmitting,
      }}
      title={isEditRedemptionLocation ? 'Edit location' : 'Create new location'}
      width='wide'
    />
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[3]};
`;

const RowContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacings[3]};
`;
